import React, { useState, useEffect } from "react";
import swal from "sweetalert";
import { AES, enc } from "crypto-js";
import { BASE_URL } from "../CommonApi";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import "./BankAccNew.css";
import Alert from "@mui/material/Alert";
import Select from "react-select";

function BankAccNew({ navigation, id, handleCancle }) {
  const encryptedUniqueId = localStorage.getItem("uniqueId1");
  const decryptedUniqueId = AES.decrypt(
    encryptedUniqueId,
    "1234567812345678"
  ).toString(enc.Utf8);
  const [selectedCurreny, setCurrency] = useState("INR");
  
  // Detect if we're in edit mode
  const isEditMode = !!id;

  const currencyOptions = [
    { value: "", label: "Select Currency" },
    { value: "INR", label: "INR - Indian Rupee" },
    { value: "AED", label: "AED - United Arab Emirates Dirham" },
    { value: "AUD", label: "AUD - Australian Dollar" },
    { value: "CAD", label: "CAD - Canadian Dollar" },
    { value: "CNY", label: "CNY - Chinese Yuan" },
    { value: "EUR", label: "EUR - Euro" },
    { value: "GBP", label: "GBP - British Pound Sterling" },
    { value: "JPY", label: "JPY - Japanese Yen" },
    { value: "SAR", label: "SAR - Saudi Riyal" },
    { value: "USD", label: "USD - United States Dollar" },
    { value: "ZAR", label: "ZAR - South African Rand" },
  ];

  const handleCurrencyChange = (selectedOption) => {
    setCurrency(selectedOption);
  };

  const handleNavigation = (key) => {
    if (navigation.state.routes[navigation.state.index].key !== key) {
      navigation.navigate(key);
    }
  };

  const handleBankClick = () => {
    // If in edit mode, use the handleCancle callback
    if (isEditMode && handleCancle) {
      handleCancle();
    } else {
      handleNavigation("bankacc");
    }
  };

  const handleCancleClick = () => {
    // Direct navigation without confirmation
    handleBankClick();
  };

  const [formData, setFormData] = useState({
    primaryAccount: "NO",
    name: "",
    accountCode: "",
    currency: "",
    accountNumber: "",
    bankName: "",
    ifsc: "",
    description: "",
    upiId: "",
    uniqueId: decryptedUniqueId,
    branchName: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Limit description to 500 characters
    if (name === 'description' && value.length > 500) {
      return;
    }
    
    // Update character count for description
    if (name === 'description') {
      setCharCount(value.length);
    }
    
    // Convert to uppercase for specific fields
    const uppercaseFields = ['bankName', 'branchName', 'name', 'ifsc'];
    const finalValue = uppercaseFields.includes(name) ? value.toUpperCase() : value;
    
    setFormData({
      ...formData,
      [name]: finalValue,
    });
  };

  const handleCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    const updatedValue = isChecked ? "YES" : "NO";

    setFormData({
      ...formData,
      primaryAccount: updatedValue,
    });
  };

  const [bankData, setBankData] = useState([]);
  const [loading, setLoading] = useState(isEditMode); // Only show loading in edit mode
  const [charCount, setCharCount] = useState(0);
  const postData = {
    uniqueId: decryptedUniqueId,
  };

  // Fetch existing bank data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchBankData();
    } else {
      fetchBanksList();
    }
  }, [id]);

  const fetchBankData = () => {
    const jwtToken = localStorage.getItem("jwtToken");
    const apiUrl = BASE_URL + `/getBankById/${id}`;

    fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ uniqueId: decryptedUniqueId }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success") {
          const bankData = data.responseMessage;
          setFormData({
            primaryAccount: bankData.primaryAccount || "NO",
            name: bankData.name || "",
            accountCode: bankData.accountCode || "",
            currency: bankData.currency || "",
            accountNumber: bankData.accountNumber || "",
            bankName: bankData.bankName || "",
            ifsc: bankData.ifsc || "",
            description: bankData.description || "",
            upiId: bankData.upiId || "",
            uniqueId: decryptedUniqueId,
            branchName: bankData.branchName || "",
          });
          
          // Set currency dropdown
          const currencyOption = currencyOptions.find(
            (option) => option.value === bankData.currency
          );
          if (currencyOption) {
            setCurrency(currencyOption);
          }
          
          // Set character count for description
          setCharCount(bankData.description?.length || 0);
          
          setLoading(false);
        } else {
          console.error("Error fetching bank data");
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching bank data:", error);
        setLoading(false);
      });
  };

  const fetchBanksList = () => {
    const jwtToken = localStorage.getItem("jwtToken");
    const apiUrl = BASE_URL + "/fetchBanks";
    
    fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify(postData),
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error("POST request failed");
        }
      })
      .then((responseData) => {
        setBankData(responseData.responseMessage);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const [validationMessage, setValidationMessage] = useState("");

  const sendData = async () => {
    const jwtToken = localStorage.getItem("jwtToken");
    const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    let newValidationMessage = "";

    if (!formData.name) {
      newValidationMessage += "Please enter Account Name.\n";
    }
    if (!selectedCurreny || !selectedCurreny.value) {
      newValidationMessage += "Please select Currency.\n";
    }
    if (!formData.accountNumber) {
      newValidationMessage += "Please enter Account Number.\n";
    }
    if (!formData.bankName) {
      newValidationMessage += "Please enter Bank Name.\n";
    }
    if (!formData.ifsc) {
      newValidationMessage += "Please enter IFSC Code.\n";
    }
    if (formData.ifsc && !ifscPattern.test(formData.ifsc)) {
      newValidationMessage += "Invalid IFSC Code format.\n";
    }

    setValidationMessage(newValidationMessage);

    if (newValidationMessage) {
      window.scrollTo(0, 0);
      return;
    }

    try {
      setLoading(true);

      const requestData = {
        ...formData,
        currency: selectedCurreny?.value || "INR",
        accountType: "Bank", // Always Bank now
      };

      let response;
      if (isEditMode) {
        // Update existing bank
        response = await fetch(BASE_URL + `/updateBank/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify(requestData),
        });
      } else {
        // Create new bank
        response = await fetch(BASE_URL + "/insertBank", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify(requestData),
        });
      }

      if (!response.ok) {
        throw new Error("Failed to save data");
      }

      const data = await response.json();
      swal({
        icon: "success",
        title: isEditMode ? "Bank Account Updated Successfully" : "Bank Account Added Successfully",
        text: data.message,
      });
      handleBankClick();
    } catch (error) {
      console.error("Error:", error);
      swal({
        icon: "error",
        title: "Network Error",
        text: "An error occurred while communicating with the server.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isPrimaryAccountExists = bankData.some(
    (item) => item.primaryAccount === "YES"
  );

  if (loading) {
    return (
      <div className="center-div">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="bank-account-wrapper">
      {/* Header */}
      <div className="bank-account-header">
        <div className="back-button" onClick={handleCancleClick}>
          <ArrowBackIosIcon className="back-icon" />
        </div>
        <div className="header-title">
          <h5 className="page-title">{isEditMode ? "Edit Bank Account" : "Add Bank Account"}</h5>
        </div>
      </div>

      {/* Form Card */}
      <div className="bank-form-card">
        {validationMessage && (
          <Alert severity="error" className="validation-alert">
            {validationMessage.split("\n").map((message, index) => (
              <div key={index}>{message}</div>
            ))}
          </Alert>
        )}

        <div className="form-header">
          <h2 className="form-title">{isEditMode ? "Edit Bank Account" : "Add Bank Account"}</h2>
          <p className="form-subtitle">
            {isEditMode ? "Update your bank account details" : "Enter your bank account details"}
          </p>
        </div>

        <div className="form-content">
          {/* Bank Details Section */}
          <div className="form-section">
            <h3 className="section-title">Bank Details</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  Account Name <span className="required">*</span>
                </label>
                <input
                  className="form-input"
                  placeholder="Enter Account Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  type="text"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Account Code</label>
                <input
                  className="form-input"
                  placeholder="Enter Account Code"
                  value={formData.accountCode}
                  name="accountCode"
                  onChange={handleInputChange}
                  type="text"
                />
              </div>

              <div className="form-group form-group-full">
                <label className="form-label">
                  Currency <span className="required">*</span>
                </label>
                <Select
                  options={currencyOptions}
                  value={selectedCurreny}
                  onChange={handleCurrencyChange}
                  placeholder="Select Currency"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      backgroundColor: "#f9fafb",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      padding: "4px",
                      minHeight: "48px",
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? "#2563eb" : state.isFocused ? "#eff6ff" : "white",
                      color: state.isSelected ? "white" : "#111827",
                    }),
                  }}
                />
              </div>
            </div>
          </div>

          {/* Account Information Section */}
          <div className="form-section">
            <h3 className="section-title">Account Information</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  Account Number <span className="required">*</span>
                </label>
                <input
                  className="form-input"
                  placeholder="Enter Account Number"
                  value={formData.accountNumber}
                  name="accountNumber"
                  onChange={handleInputChange}
                  type="text"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Bank Name <span className="required">*</span>
                </label>
                <input
                  className="form-input"
                  placeholder="Enter Bank Name"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleInputChange}
                  type="text"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  IFSC Code <span className="required">*</span>
                </label>
                <input
                  className="form-input"
                  placeholder="Enter IFSC Code"
                  value={formData.ifsc}
                  name="ifsc"
                  onChange={handleInputChange}
                  type="text"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Branch Name</label>
                <input
                  className="form-input"
                  placeholder="Enter Branch Name"
                  value={formData.branchName}
                  name="branchName"
                  onChange={handleInputChange}
                  type="text"
                />
              </div>

              <div className="form-group form-group-full">
                <label className="form-label">Description</label>
                <div className="textarea-wrapper">
                  <textarea
                    rows="5"
                    placeholder="Enter description (optional)"
                    className="form-textarea"
                    value={formData.description}
                    name="description"
                    onChange={handleInputChange}
                  ></textarea>
                  <div className="char-counter">
                    <span className={charCount > 450 ? 'char-warning' : ''}>
                      {charCount}
                    </span>
                    <span className="char-limit"> / 500</span>
                  </div>
                </div>
              </div>

              <div className="form-group form-group-full">
                <label className="form-label">UPI ID</label>
                <input
                  className="form-input"
                  placeholder="Enter Your UPI ID (optional)"
                  value={formData.upiId}
                  name="upiId"
                  onChange={handleInputChange}
                  type="text"
                />
              </div>
            </div>
          </div>

          {/* Primary Account Checkbox */}
          <div className="form-section">
            <div className="checkbox-group">
              <input
                id="primaryAccount"
                className="form-checkbox"
                type="checkbox"
                name="primaryAccount"
                checked={formData.primaryAccount === "YES"}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="primaryAccount" className="checkbox-label">
                Make this primary account
              </label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button className="btn-cancel" onClick={handleCancleClick}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={sendData}
          >
            {isEditMode ? "Update Bank Account" : "Save Bank Account"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BankAccNew;
